import { message } from 'antd';
import { push } from 'connected-react-router';
import { filterDeep } from 'deepdash-es/standalone';
import { uniqBy } from 'lodash';
import { QueryClient } from 'react-query';
import { all, put, select, takeLatest } from 'redux-saga/effects';
import { Role } from 'api/models';
import {
  getAutoAllocatedAllocationsFromTasks,
  getAutoAllocatedAssignees,
} from 'components/organisms/templates/projects/resource-management/utils/auto-allocate';
import { RootState } from 'data';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { TemplatesAPI } from 'data/templates/api';
import { saveDocumentTemplate } from 'data/templates/documents/sagas/_save-template';
import {
  ROLES_QUERY_KEY,
  ROLES_WITH_PLACEHOLDER_QUERY_KEY,
} from 'queries/roles';
import { logger } from 'services';
import { TeamsStore } from 'stores/teams';
import {
  changeTemplateSaveState,
  clearProjectTemplate,
  fetchProjectTemplateStartSuccess,
} from '..';
import { saveProjectTemplate, SaveProjectTemplatePayload } from '../actions';
import {
  denormalizePayload,
  NormalizedProjectTemplatePayload,
  setDynamicConditionInPayload,
  validatePhaseShiftInPayload,
} from '../api/transform';
import {
  ProjectTemplateApi,
  ProjectTemplateData,
  ProjectTemplateSpace,
  TemplateAllocation,
} from '../types';
import { findLargestSumOfStartDayDuration, findLowestStartDay } from '../utils';
import { generateNewTaskRows } from './utils';

export const getActiveProjectTemplate = ({
  templates,
}: RootState): NormalizedProjectTemplatePayload =>
  templates.project.data[templates.project.activeId];

function* saveAllSpaceTabs(spaces: ProjectTemplateSpace[]) {
  const saveAllSpaces: any[] = [];

  spaces.forEach((space) => {
    space.spaceTabs.forEach((tab) => {
      saveAllSpaces.push(saveDocumentTemplate(tab.spaceTabId, true));
    });
  });

  yield all(saveAllSpaces);
}

function* saveProjectTemplateSaga(
  action: StoreActionProps<SaveProjectTemplatePayload>,
) {
  const {
    payload: { type, handleSuccess },
  } = action;

  const projectTemplate: NormalizedProjectTemplatePayload = yield select(
    getActiveProjectTemplate,
  );

  const queryClient = (yield getQueryClientContext()) as QueryClient;

  try {
    yield put(
      changeTemplateSaveState({
        state: true,
        template: projectTemplate.template,
      }),
    );
    const { autoAllocation: autoAllocate } =
      projectTemplate.template.templateContent;
    const _template = setDynamicConditionInPayload(projectTemplate);

    const _phaseShiftValidTemplate = validatePhaseShiftInPayload(_template);

    const template = denormalizePayload(_phaseShiftValidTemplate);

    template.templateContent.projectPhases =
      filterDeep(
        template.templateContent.projectPhases,
        (object) => {
          if (object.isNew) {
            return false;
          }

          // eslint-disable-next-line no-param-reassign
          delete object.isNew;

          return true;
        },
        { childrenPath: ['tasks', 'subTasks'] },
      ) || [];

    const largestDuration = findLargestSumOfStartDayDuration(
      template.templateContent
        .projectPhases as unknown[] as ProjectTemplateData[],
    );
    const lowestStartDay = findLowestStartDay(
      template.templateContent
        .projectPhases as unknown[] as ProjectTemplateData[],
    );

    const total = largestDuration - lowestStartDay;

    template.templateContent.duration = total;
    template.templateContent.startDay = lowestStartDay;
    template.state = type;

    if (autoAllocate === true) {
      const tasks = Object.values(projectTemplate.tasks);
      const allocatedMembers = getAutoAllocatedAssignees(tasks);
      const allocationsWithType = getAutoAllocatedAllocationsFromTasks(tasks);
      const allocations = allocationsWithType.map(
        ({ type: resourceType, ...rest }) => rest as TemplateAllocation,
      );

      template.templateContent.allocations = allocations;
      template.templateContent.allocatedMembers = allocatedMembers;
    } else {
      const allocationsWithType = Object.values(
        projectTemplate.allocations || {},
      );
      const allocations = allocationsWithType.map(
        ({ type: resourceType, ...rest }) => rest as TemplateAllocation,
      );

      template.templateContent.allocations = allocations;
      template.templateContent.allocatedMembers =
        projectTemplate.allocatedMembers;
    }
    /**
     * Since server saves the template json as dump without validation,
     * FE sorts the phases by startDay before server submit
     * to maintain the chronological order during project creation from template.
     */
    template.templateContent.projectPhases = [
      ...template.templateContent.projectPhases,
    ].sort((phase1, phase2) => phase1.startDay - phase2.startDay);
    // temporary code to remove duplicate spaces
    template.templateContent.spaceList = uniqBy(
      template.templateContent.spaceList,
      (x) => x.spaceId,
    );

    yield saveAllSpaceTabs(template.templateContent.spaceList);

    const updatedTemplate: ProjectTemplateApi =
      yield TemplatesAPI.save<ProjectTemplateApi>(template);

    if (handleSuccess) {
      handleSuccess(updatedTemplate);
    }
    const templateWithNewTaskRows = generateNewTaskRows(updatedTemplate);

    const rolesWithPlaceholder = queryClient.getQueryData<Role[]>(
      ROLES_WITH_PLACEHOLDER_QUERY_KEY,
    );
    const roles = queryClient.getQueryData<Role[]>(ROLES_QUERY_KEY);
    const { byId: teamsById }: TeamsStore = yield select(
      ({ teams }: RootState) => teams,
    );

    yield put(
      fetchProjectTemplateStartSuccess({
        template: templateWithNewTaskRows,
        rolesWithPlaceholder,
        allRoles: roles,
        teamsLookup: teamsById,
      }),
    );

    if (template.templateId === 'new') {
      yield put(clearProjectTemplate(projectTemplate.template));

      yield put(push(`/templates/projects/${updatedTemplate.templateId}`));

      message.success('Template created successfully!');
    } else {
      message.success('Template saved successfully!');
    }

    yield put(
      changeTemplateSaveState({
        state: false,
        template: {
          templateId: updatedTemplate.templateId,
        },
      }),
    );
  } catch (error) {
    logger.error('an error while saving the template', { error });

    message.error('There was an error while saving the template');

    yield put(
      changeTemplateSaveState({
        state: false,
        template: projectTemplate.template,
      }),
    );
  }
}

export function* watchSaveProjectTemplateSaga(): Generator {
  yield takeLatest(saveProjectTemplate.type, saveProjectTemplateSaga);
}
